/* eslint-disable camelcase */
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { isEmpty } from 'lodash';
import { ErrorBoundary } from 'react-error-boundary';
import mixpanel from 'mixpanel-browser';
import * as dayjs from 'dayjs';
import 'react-toastify/dist/ReactToastify.css';
import FullStory, { identify } from 'react-fullstory';
import {
  Route, Switch, Redirect, useHistory,
} from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';
import Layout from './components/Layout';
import DriverLogin from './views/DriverLogin';
import { setCookie, deleteCookie } from './components/Cookie';

import BroadcastNotification from './components/BroadcastNotification';
import {
  IMEI, socket, changeTabletBrightness, changeTabletVolume,
} from './socket';
import * as serviceWorker from './serviceWorker';
import {
  getObject, setObject, generateCode, generateCodeInt,
} from './utils/helper';
import { isProduction } from './config';
import { defaultTiles } from './utils/defaultTiles';

import NewWelcome from './views/NewWelcome';
import AppContainer from './views/AppContainer';
import SettingsModal from './components/SettingsModal';
import ReloadMessage from './components/ReloadMessage';
import './App.css';
import {
  getDriverInfo, getLocalNews, getTiles, getVideoElephantChannels, getBroadcasts, reportImpressions, reportHeartbeat,
} from './api';
import AboutDriver from './views/Welcome/AboutDriver';
import { ContentContext } from './context/content';
import { AuthContext } from './context/auth';
import { ControlsContext } from './context/controls';
import { NewsContext } from './context/news';
import BroadcastMessage from './components/BroadcastMessage';
import Theme from './components/Theme';

import { getAnalyticsInfo, trackMixpanel } from './analytics';
import { getLastLocation } from './store/location';
import DeprecatedTablet from './views/DeprecatedTablet';
import CheckingForUpdates from './views/CheckingForUpdates';
import ErrorFallback from './views/ErrorFallback';
import PhotoConfirmation from './views/PhotoConfirmation';
import {
  LowBatteryContainer, StyledToastContainer, StyledToastContainerTopLeft,
  RemoteControlPopup, CodeWrapper, CodeNumber, AnimatedTimerWrapper, SuccessControl,
  PhoneIcon, RiderOverlay,
} from './appStyle';

// themes
import midnightBlack from './themes/midnight-black';
import arcticWhite from './themes/arctic-white';

export const heartBeatInterval = 5 * 60 * 1000; // 5 minutes
const duration = heartBeatInterval / 1000;
const impressionsInterval = 5 * 60 * 1000; // 5 mins;

const feedbackToastId = 'broadcast';

const LowBatteryIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    height="100%"
    viewBox="0 0 24 24"
    width={130}
  >
    <path d="M0 0h24v24H0z" fill="none" />
    <path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 0 1.34-.6 1.34-1.33V5.33C17 4.6 16.4 4 15.67 4zM13 18h-2v-2h2v2zm0-4h-2V9h2v5z" />
  </svg>
);

const initialProgrammaticState = {
  hivestackAdReq: 0,
  hivestackAdPlayed: 0,
  broadsignAdReq: 0,
  broadsignAdPlayed: 0,
  vistarAdReq: 0,
  vistarAdPlayed: 0,
  adomniAdReq: 0,
  adomniAdPlayed: 0,
};

Sentry.init({
  dsn: 'https://c3f2476826734dbd9bdecc128f0498b8@o4504010516004864.ingest.sentry.io/4504015912501248',
  integrations: [new BrowserTracing()],

  tracesSampleRate: 1.0,
});

const App = () => {
  const [driver, setDriver] = useState();
  const [broadcast, setBroadcast] = useState(false);
  const [SWState, setSWState] = useState({
    waitingWorker: {},
    newVersionAvailable: false,
  });
  const [tiles, setTiles] = useState(defaultTiles);
  const [adLoop, setAdLoop] = useState(undefined);
  const [theme, setTheme] = useState(arcticWhite);
  const [privateCode, setPrivateCode] = useState(generateCode(15));
  const [stopTimer, setStopTimer] = useState(false);
  const [riderIsConnected, setRiderIsConnected] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [newsArticles, setNewsArticles] = useState();

  const programmaticCounts = useRef({ ...initialProgrammaticState });

  const history = useHistory();
  const impressionsRef = useRef({});

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isEmpty(impressionsRef.current)) {
        reportImpressions(impressionsRef.current);
      }

      impressionsRef.current = {};
    }, impressionsInterval);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const saveImpression = async (creativeId) => {
    const position = await getLastLocation();

    // important line of code, otherwise position will be overwritten for some reason (probably some memory reference)
    const impressionPos = { ...position };

    const creativeImpressionsArray = impressionsRef.current[creativeId];
    if (!creativeImpressionsArray) {
      // eslint-disable-next-line no-param-reassign
      impressionsRef.current[creativeId] = [];
    }

    impressionsRef.current[creativeId].push(impressionPos);
  };

  const authenticate = async () => {
    // // local development
    //
    // const localDriver = {
    //   _id: '64ef40cc6b45957061752511', lastModified: 1596650709434, profile: 'https://driver-profile.s3.us-east-1.amazonaws.com/jay@portlmedia.com1584732538710.png', created: 1575913428573, referralCode: 'TNXM86', email: 'serban.movila@digitalya.co', email_verified: true, approved: false, agreed: '"2019-12-09T17:45:06.548Z"', rideshareServices: '---', cities: '---', driverName: { last: 'Movila', first: 'Serban' }, leadStatus: 'Installed', phone: '6475158161', tablets: [{ tabletId: 'daa050d0-37d6-11ea-8dc9-c7e5d2fa7722', IMEI: '358510073288496', dateAdded: 1579189640468 }, { tabletId: 'a8643fe0-37d7-11ea-9963-a3018b3071b6', IMEI: '358510073289262', dateAdded: 1579193193038 }, { tabletId: 'b6eae690-4123-11ea-b31a-b31e742ffc1a', IMEI: '358510073288579', dateAdded: 1580309913275 }, { tabletId: '721da3a0-4393-11ea-af55-2f979d54254d', IMEI: '358510073285179', dateAdded: 1580411174064 }, { tabletId: '55f417b0-43a0-11ea-8f63-1f9873bd9fb8', IMEI: '358510073289197', dateAdded: 1580417133575 }, { tabletId: '04476080-443a-11ea-acaa-715ecc208ffe', IMEI: '358510073288363', dateAdded: 1580927725195 }, { tabletId: '04476080-443a-11ea-acaa-715ecc208ffe', IMEI: '358510073288363', dateAdded: 1580927725194 }, { tabletId: '6b498100-49bc-11ea-b753-3ff6c6776a03', IMEI: '358510073287217', dateAdded: 1581088535823 }, { tabletId: '6b498100-49bc-11ea-b753-3ff6c6776a03', IMEI: '358510073287217', dateAdded: 1581088535822 }, { tabletId: 'e51508b0-49e9-11ea-a341-a36b6469ce60', IMEI: '358510073289452', dateAdded: 1581108300614 }, { tabletId: 'e51508b0-49e9-11ea-a341-a36b6469ce60', IMEI: '358510073289452', dateAdded: 1581108300617 }, { tabletId: '82618130-4e80-11ea-88b4-a55f469dc209', IMEI: '358510073264786', dateAdded: 1581612458986 }, { tabletId: '82618130-4e80-11ea-88b4-a55f469dc209', IMEI: '358510073264786', dateAdded: 1581612459166 }, { tabletId: '7ce595b0-4e81-11ea-b6d8-f935ed3432cb', IMEI: '358510073264786', dateAdded: 1581612950459 }, { tabletId: '7ce595b0-4e81-11ea-b6d8-f935ed3432cb', IMEI: '358510073264786', dateAdded: 1581612950457 }, { tabletId: '35a7a620-528c-11ea-a592-79699e217aed', IMEI: '358510073284297', dateAdded: 1582128971714 }, { tabletId: '35a7a620-528c-11ea-a592-79699e217aed', IMEI: '358510073284297', dateAdded: 1582128971964 }, { tabletId: '3820dad0-53fd-11ea-84db-2f2b7c5ea8d7', IMEI: '358510073269959', dateAdded: 1582216413702 }, { tabletId: '3820dad0-53fd-11ea-84db-2f2b7c5ea8d7', IMEI: '358510073269959', dateAdded: 1582216413834 }, { tabletId: 'f39687c0-5410-11ea-b2d6-f5bfe0e7170f', IMEI: '358510073267417', dateAdded: 1582293253187 }, { tabletId: 'f39687c0-5410-11ea-b2d6-f5bfe0e7170f', IMEI: '358510073267417', dateAdded: 1582293253185 }, { tabletId: 'f18003d0-5410-11ea-a453-932fc784ea21', IMEI: '358510073267409', dateAdded: 1582293260663 }, { tabletId: 'f18003d0-5410-11ea-a453-932fc784ea21', IMEI: '358510073267409', dateAdded: 1582293260665 }, { tabletId: '7a4d69d0-57f1-11ea-9159-6138c1ec0bce', IMEI: '358510073280816', dateAdded: 1582650575801 }, { tabletId: '7a4d69d0-57f1-11ea-9159-6138c1ec0bce', IMEI: '358510073280816', dateAdded: 1582650575799 }, { tabletId: 'a4f81030-5806-11ea-b34a-59e8c74336d1', IMEI: '358510073287779', dateAdded: 1582661161655 }, { tabletId: 'a4f81030-5806-11ea-b34a-59e8c74336d1', IMEI: '358510073287779', dateAdded: 1582661161659 }, { IMEI: '358510073279941', dateAdded: 1597245916837, tabletId: '3cbd06f0-dcaf-11ea-b899-4734b158e3f6' }, { IMEI: '358510073279941', dateAdded: 1597245917083, tabletId: '3cbd06f0-dcaf-11ea-b899-4734b158e3f6' }, { IMEI: '358510073280238', dateAdded: 1608564483183 }, { IMEI: '358510073280238', dateAdded: 1608564484803 }, { IMEI: '358510073266302', dateAdded: 1615401205814 }, { IMEI: '358510073266302', dateAdded: 1615401206102 }, { IMEI: '358510073266302', dateAdded: 1639415145079 }, { IMEI: '358510073266302', dateAdded: 1639415145351 }, { IMEI: '358510073283976', dateAdded: 1648657582496 }, { IMEI: '358510073283976', dateAdded: 1648657582742 }, { IMEI: '358510073286565', dateAdded: 1656512469849 }, { IMEI: '358510073286565', dateAdded: 1656512470017 }, { IMEI: '358510073286565', dateAdded: 1656512471314 }, { IMEI: '358510073284180', dateAdded: 1662564121488 }, { IMEI: '358510073284180', dateAdded: 1662564121618 }, { IMEI: '358510073284180', dateAdded: 1662564121723 }, { IMEI: '358510073288785', dateAdded: 1662649155100 }, { IMEI: '358510073288785', dateAdded: 1662649155251 }, { IMEI: '358510073288785', dateAdded: 1662649156549 }, { IMEI: '358510073284651', dateAdded: 1679682482769 }, { IMEI: '358510073284651', dateAdded: 1679682483896 }, { IMEI: '358510073284594' }, { IMEI: '358510073284594', dateAdded: 1693854878600 }, { IMEI: '358510073284594', dateAdded: 1693854878964 }, { IMEI: '358510073284594', dateAdded: 1693994991674 }, { IMEI: '358510073284594', dateAdded: 1693994992110 }], carMake: 'NISSAN', carModel: 'Altima', carYear: 2017,
    // };
    // // const localDriver = getObject('driver');
    // if (localDriver) {
    //   setDriver(localDriver);
    //   return;
    // }

    const driverResponse = await getDriverInfo();
    delete driverResponse.stats;

    if (driverResponse.error && !driverResponse.error.response) {
      // network error, try and pull driver email from local storage for now
      const localDriver = getObject('driver');
      if (localDriver) {
        console.log('tablet offline. Fetching driver from localstorage. ');

        mixpanel.identify(localDriver.email);
        mixpanel.people.set({ $email: localDriver.email });
        setDriver(localDriver);
      }
      // set timeout to refresh in 60s
      return setTimeout(authenticate, 60000);
    }
    // authentication error
    if (driverResponse.error) {
      localStorage.removeItem('driver');
    } else {
      const { email, province } = driverResponse;
      mixpanel.identify(email);
      mixpanel.people.set({ $email: email });
      // fullstory
      if (isProduction) {
        identify(email, { province, email, imei: IMEI });
      }
      setObject('driver', driverResponse);
    }

    return setDriver(driverResponse);
  };

  // Check for refreshes
  useEffect(() => {
    if (!driver) {
      authenticate();
    }
    // eslint-disable-next-line
  }, [driver]);

  const rad = (x) => (x * Math.PI) / 180;

  const getDistance = (lat1, lat2, lng1, lng2) => {
    const R = 6378137; // Earth’s mean radius in meter
    const dLat = rad(lat2 - lat1);
    const dLong = rad(lng2 - lng1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
      + Math.cos(rad(lat1)) * Math.cos(rad(lat2))
      * Math.sin(dLong / 2) * Math.sin(dLong / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c;
    return d; // returns the distance in meter
  };

  useEffect(() => {
    let isMounted = true;
    let id;
    let coords;
    let broadcasts;
    let distance;

    const fetchBroadcasts = async () => {
      broadcasts = await getBroadcasts();
    };

    const fetchLocation = async () => {
      coords = await getLastLocation();
      if (broadcasts && broadcasts.length > 0) {
        // eslint-disable-next-line array-callback-return
        broadcasts?.map((item) => {
          distance = getDistance(coords.lat, item.lat, coords.lng, item.lng);

          if (isMounted && broadcasts.length > 0 && distance / 1000 < item.radius) {
            toast(<BroadcastNotification broadcast={item} />, {
              delay: 2000, autoClose: 15 * 1000, toastId: feedbackToastId, closeOnClick: false, containerId: 'B',
            });
          }
        });
      }
    };

    const locationTimeout = setInterval(fetchLocation, 30 * 1000);
    const broadcastsTimeout = setInterval(fetchBroadcasts, 5 * 60 * 1000);
    fetchLocation();
    fetchBroadcasts();

    return () => {
      isMounted = false;
      clearTimeout(id);
      clearInterval(locationTimeout);
      clearInterval(broadcastsTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isMounted = true;
    let id;

    const fetchTiles = async () => {
      const response = await getTiles();
      const channels = await getVideoElephantChannels();
      const videoElephantChannels = [];
      if (channels && Array.isArray(channels)) {
        channels?.forEach((element) => {
          videoElephantChannels.push({ ...element, videoElephant: true });
        });
      }

      if (isMounted && response.length > 0) {
        setTiles([...response, ...videoElephantChannels]);
      }
    };

    const tilesTimeout = setTimeout(() => {
      fetchTiles();
    }, 5 * 60 * 1000);

    fetchTiles();
    return () => {
      isMounted = false;
      clearTimeout(id);
      clearTimeout(tilesTimeout);
    };
    // eslint-disable-next-line
  }, []);

  // check low battery
  useEffect(() => {
    const thirtyMinsAgo = () => dayjs().subtract(30, 'minutes');
    let lastNotified = thirtyMinsAgo();
    let batInterval;

    const checkLowBattery = () => {
      const isLow = window.DeviceApi.getBatteryLevel() <= 15;
      const isCharging = window.DeviceApi.getBatteryCharging();

      if (!isCharging && isLow && lastNotified.isBefore(thirtyMinsAgo())) {
        lastNotified = dayjs(new Date());
        toast.warn(
          <LowBatteryContainer data-test-id="low-battery">
            <LowBatteryIcon />
            <p>
              Please help your driver out by letting them know the battery is low!
            </p>
          </LowBatteryContainer>,
        );
      }
    };

    if (
      window.DeviceApi
      && window.DeviceApi.getBatteryLevel
      && window.DeviceApi.getBatteryCharging
    ) {
      checkLowBattery();
      batInterval = setInterval(checkLowBattery, 20000); // check every 20s
    }

    return () => batInterval && clearInterval(batInterval);

    // eslint-disable-next-line
  }, []);

  const incrementProgrammatic = (event) => {
    // event is hivestackAdReq, hivestackAdPlayed, etc.
    if (typeof programmaticCounts.current[event] === 'number') {
      programmaticCounts.current[event] += 1;
    }
  };

  // Service Worker registration
  useEffect(() => {
    const updateServiceWorker = () => {
      const { waitingWorker } = SWState;
      console.log('Service worker updating: ', waitingWorker);
      setSWState((prevState) => ({ ...prevState, newVersionAvailable: false }));
      window.location.reload();
    };

    // eslint-disable-next-line no-shadow
    const onServiceWorkerUpdate = (serviceWorker) => {
      setSWState({
        waitingWorker: serviceWorker,
        newVersionAvailable: true,
      });
      updateServiceWorker();
    };

    // eslint-disable-next-line no-shadow
    const onServiceWorkerInstalling = (serviceWorker) => {
      console.log('Service worker installing: ', serviceWorker);
      setSWState({
        waitingWorker: serviceWorker,
        newVersionAvailable: true,
      });
      serviceWorker.addEventListener('statechange', () => {
        if (serviceWorker.state === 'installed') {
          updateServiceWorker();
        }
      });
    };

    serviceWorker.register({
      onUpdate: onServiceWorkerUpdate,
      onInstalling: onServiceWorkerInstalling,
    });

    // eslint-disable-next-line
  }, []);

  const driverLogout = () => {
    console.log('logging out driver');
    deleteCookie('token');
    localStorage.removeItem('strapiToken');
    localStorage.removeItem('driver');
    mixpanel.reset();
    window.location.href = '/';
  };

  // Heartbeat & sockets
  useEffect(() => {
    const intervals = [];
    const mixpanelHeartbeat = async () => {
      let analyticsInfo = await getAnalyticsInfo();
      // let cache = undefined;
      // if (navigator.storage.estimate) {
      //   cache = await navigator.storage.estimate();
      // }

      analyticsInfo = {
        ...analyticsInfo,
        duration: heartBeatInterval / 1000, // conversion to seconds
        // pathname: document.location.pathname,
        // ...cache,
        ...programmaticCounts.current,
        batteryLevel:
          window.DeviceApi
          && window.DeviceApi.getBatteryLevel
          && window.DeviceApi.getBatteryLevel(),
        batteryCharging:
          window.DeviceApi
          && window.DeviceApi.getBatteryCharging
          && window.DeviceApi.getBatteryCharging(),
      };

      console.log('Mixpanel Heartbeat: ', analyticsInfo);
      mixpanel.track('Heartbeat', analyticsInfo);
      programmaticCounts.current = { ...initialProgrammaticState }; // reset for next heartbeat
    };

    const data = {};
    let i = 0;

    const POSITION_INTERVAL = 5; // in seconds;
    const gatherPosition = async () => {
      const coords = await getLastLocation();
      data[i % duration] = { ...coords };
      if (window.DeviceApi && window.DeviceApi.faceDetected) {
        data[i % duration].face = window.DeviceApi.faceDetected();
      }
      i += POSITION_INTERVAL;

      // report to server every heartbeat interval
      if (i % duration === 0) {
        console.log('Emiting heartbeat!!!');
        reportHeartbeat(duration, data);
      }
    };

    if (window.DeviceApi && window.DeviceApi.getAppVersion) {
    //   console.log('Logging...');
    // gather face detection, position every 5 seconds and report every 5 mins
      intervals.push(setInterval(gatherPosition, POSITION_INTERVAL * 1000));
    }

    intervals.push(setInterval(mixpanelHeartbeat, heartBeatInterval));

    socket.on('broadcast', (message) => {
      setBroadcast(message);
    });

    socket.on('auth', async (body) => {
      // eslint-disable-next-line no-shadow
      let IMEI;
      try {
        IMEI = window.DeviceApi.getImei(); // only works for webview
        console.log('Device IMEI: ', IMEI);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          IMEI = localStorage.getItem('IMEI');
        } else console.log('Error fetching IMEI: ', error);
      }
      socket.emit('auth_ack', {
        email: driver ? driver.email : undefined,
        IMEI,
      });
      if (IMEI) {
        setCookie('token', body.token, 1000 * 365 * 24 * 60 * 60 * 1000);
        // socket.emit("confirm", { client: IMEI });
        authenticate();
      }
      return null;
    });

    socket.on('changeTheme', (darkMode) => {
      if (darkMode) return setTheme(midnightBlack);
      return setTheme(arcticWhite);
    });

    socket.on('confirmSetup', () => {
      authenticate();
    });

    socket.on('reload', () => {
      const reloadPage = () => window.location.replace(window.location.origin);
      // eslint-disable-next-line no-use-before-define
      const resetTimer = () => setTimeout(reloadMessage, 1000 * 60 * 10);

      const reloadMessage = () => toast.warn(
        <ReloadMessage resetTimer={resetTimer} reloadPage={reloadPage} />,
        {
          autoClose: false,
        },
      );

      reloadMessage();
    });

    socket.on('logout', driverLogout);
    return () => {
      socket.off('reload');
      socket.off('broadcast');
      socket.off('auth');
      socket.off('changeTheme');
      socket.off('confirmSetup');
      socket.off('logout');
      intervals.forEach((interval) => clearInterval(interval));
    };
    // eslint-disable-next-line
  }, []);

  const disconnectedRider = () => {
    if (!riderIsConnected) return;

    setRiderIsConnected(false);
    setStopTimer(false);
    toast.dismiss();
    socket.emit('sessionEnded');
  };

  // Tablet remote control config
  useEffect(() => {
    let codeTimeoutId;
    let renewTimerId;

    const startSessionTimer = () => {
      clearTimeout(renewTimerId);

      renewTimerId = setTimeout(() => {
        const renewCode = generateCodeInt(4);
        setStopTimer(renewCode);
        socket.emit('sendRenewCode', renewCode);
      }, 15 * 60 * 1000); // show the code after 15 min
    };

    socket.on('tabletRemoteRequest', (code) => {
      let res = 'declined';

      if (code === privateCode) {
        toast.dismiss();
        res = 'accepted';
      }

      socket.emit('tabletRemoteResponse', res);
    });

    socket.on('tabletConnected', () => {
      setStopTimer(false);
      setRiderIsConnected(true);
      startSessionTimer();
      codeTimeoutId = setTimeout(() => {
        setPrivateCode(generateCode(15)); // reset the private code in 2 min after using it
      }, 2 * 60 * 1000);
      toast.success(
        <SuccessControl>
          <PhoneIcon />
          Your mobile device was connected successfully!
        </SuccessControl>,
        { autoClose: 5000 },
      );
    });

    socket.on('goToApp', (tileName) => {
      const code = generateCode(15);
      history.push(`/app/${tileName}?remoteControl=${code}`);
      socket.emit('appOpened', tileName, code);
      trackMixpanel('AppMobileStart', { name: tileName });
    });

    socket.on('goToHome', () => {
      history.push('/');
      socket.emit('homeOpened');
    });

    socket.on('changeSetting', (setting, level) => {
      if (setting === 'volume') changeTabletVolume(level);

      if (setting === 'brightness') changeTabletBrightness(level);

      socket.emit('settingChanged');
    });

    socket.on('renewConfirmed', () => {
      toast.dismiss();
      setStopTimer(false);
      startSessionTimer();
    });

    socket.on('endSession', () => {
      disconnectedRider();
    });

    return () => {
      socket.off('tabletRemoteRequest');
      socket.off('goToApp');
      socket.off('goToHome');
      socket.off('changeSetting');
      socket.off('sendRenewCode');
      socket.off('renewConfirmed');
      socket.off('endSession');
      socket.off('tabletConnected');
      clearTimeout(codeTimeoutId);
      clearTimeout(renewTimerId);
    };
    // eslint-disable-next-line
  }, [privateCode]);

  // Fetch news articles every hour
  useEffect(() => {
    let isMounted = true;
    const fetchNewsArticles = async () => {
      const response = await getLocalNews();
      if (response.error || !response.articles) {
        return console.log('Error fetching news: ', response.error);
      }
      const { articles } = response;
      return isMounted && setNewsArticles(articles);
    };

    fetchNewsArticles();
    const intervalId = setInterval(() => {
      fetchNewsArticles();
      // fetch news every hour
    }, (1000 * 60 * 60));

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, []);

  const closeBroadcast = () => {
    setBroadcast(undefined);
  };

  // const hours = new Date().getHours();
  // const isDay = hours >= 6 && hours < 18;

  // // If nighttime, switch to dark mode
  // if (!isDay && state.theme !== themes["midnight-black"]) {
  //   setState({ theme: themes["midnight-black"] });
  // }
  // // If daytime, switch to light mode
  // else if (isDay && state.theme !== themes["arctic-white"]) {
  //   setState({ theme: themes["arctic-white"] });
  // }
  if (
    (!window.DeviceApi || !window.DeviceApi.getAppVersion)
    && process.env.REACT_APP_ENV === 'production'
  ) {
    // Render static page for Hexnode tablet [No longer supported]
    return <DeprecatedTablet />;
  }

  if (!driver || SWState.newVersionAvailable) {
    return (
      <CheckingForUpdates>
        {SWState.newVersionAvailable ? 'Updating' : 'Authenticating'}
      </CheckingForUpdates>
    );
  }

  if (driver.error) {
    return (
      <Theme theme={midnightBlack}>
        <DriverLogin id={IMEI} />
      </Theme>
    );
  }

  if (!driver.error && driver.leadStatus === 'TabletShipped') {
    // Dark theme is requered on this section
    return (
      <Theme theme={midnightBlack}>
        <PhotoConfirmation id={IMEI} setDriver={setDriver} />
      </Theme>
    );
  }

  const FULLSTORY_ORG_ID = '116NR7';
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => window.location.replace(window.location.origin)}
    >
      <ControlsContext.Provider
        value={{
          updateBrightness: changeTabletBrightness,
          updateVolume: changeTabletVolume,
        }}
      >
        <AuthContext.Provider value={{ driver, setDriver }}>
          <ContentContext.Provider
            value={{
              adLoop,
              setAdLoop,
              tiles,
              setTiles,
              stopTimer,
              showSettings,
              incrementProgrammatic,
            }}
          >
            {isProduction && <FullStory org={FULLSTORY_ORG_ID} />}
            <Theme theme={theme}>
              <Layout>
                {/* <TimeoutPopup> */}
                {riderIsConnected && <RiderOverlay onClick={disconnectedRider} />}
                {stopTimer && (
                  <RemoteControlPopup>
                    <AnimatedTimerWrapper
                      time={60}
                      trailColor="#0069ba"
                      color="#ffffff"
                      onTimerComplete={disconnectedRider}
                      isPlaying
                      size={160}
                      keyId={stopTimer}
                    />
                    <CodeWrapper>
                      Your mobile Portl session has timed out. In order to continue
                      please enter the code below on your mobile device.
                      <CodeNumber>{stopTimer}</CodeNumber>
                    </CodeWrapper>
                  </RemoteControlPopup>
                )}
                <StyledToastContainer
                  limit={2}
                  position="bottom-left"
                  autoClose={15000}
                  closeButton={false}
                  hideProgressBar
                  closeOnClick
                  pauseOnFocusLoss={false}
                  draggable
                  pauseOnHover={false}
                  enableMultiContainer
                  containerId="A"
                />
                <StyledToastContainerTopLeft
                  limit={2}
                  position="top-left"
                  autoClose={15000}
                  closeButton={false}
                  hideProgressBar
                  closeOnClick
                  pauseOnFocusLoss={false}
                  draggable
                  pauseOnHover={false}
                  enableMultiContainer
                  containerId="B"
                />
                <BroadcastMessage
                  isOpen={broadcast}
                  closeBroadcast={closeBroadcast}
                  message={broadcast}
                />
                <SettingsModal
                  showOverlay={showSettings}
                  setShowOverlay={setShowSettings}
                />
                <Switch>
                  <Route path="/" exact>
                    <NewsContext.Provider value={{ newsArticles }}>
                      <NewWelcome
                        incrementProgrammatic={incrementProgrammatic}
                        privateCode={privateCode}
                        setShowSettings={setShowSettings}
                        saveImpression={saveImpression}
                      />
                    </NewsContext.Provider>
                  </Route>
                  <Route path="/app/:name">
                    <AppContainer
                      setShowSettings={setShowSettings}
                      riderIsConnected={riderIsConnected}
                    />
                  </Route>
                  <Route exact path="/driver">
                    <AboutDriver driverLogout={driverLogout} open driver={driver} />
                  </Route>
                  {/* <Route exact path="/" render={() => <Redirect exact to="/home" />} /> */}
                  {/* <Route exact path="/welcome"
                        render={() => (<WelcomeInterim hivestackAdReq={incrementHivestackAdReq}
                        hivestackAdPlayed={incrementHivestackAdPlayed} />)}
                /> */}
                  {/* <ContentRoute path="/video" render={() => (<Video />)} /> */}
                  {/* <ContentRoute exact path="/sub/:parentid"
                  render={({ match }) => <Sub parentID={match.params.parentid} />}
                /> */}
                  {/* <ContentRoute exact path="/app/" component={Microsite} /> */}
                  {/* <ContentRoute exact path="/widget/:name"
                  render={props => <Widget siteUrl={props.match.params.name} />}
                /> */}
                  {/* <ContentRoute exact path="/article/" render={() => <Article />} /> */}
                  {/* <ContentRoute exact path="/rss" render={() => <RssFeed />} /> */}

                  {/* <ContentRoute component={Error} /> */}
                  <Redirect to="/" />
                </Switch>
                {/* </TimeoutPopup> */}
              </Layout>
            </Theme>
          </ContentContext.Provider>
        </AuthContext.Provider>
      </ControlsContext.Provider>
    </ErrorBoundary>
  );
};

export default App;
